import { useMemo } from 'react';
import type { WidgetProps, ChartDataPoint, FieldMappingOverride } from '../types';
import { extractChartContract } from '../types';
import { useWidgetFetch } from './useWidgetFetch';
import { aggregateChartData } from '../utils/aggregateChartData';
import { resolveChartFields } from '../utils/resolveChartFields';

export interface ChartDataResult {
  chartData: Record<string, unknown>[];
  valueKeys: string[];
  categoryField: string;
  aliases: Record<string, string>;
  data: ChartDataPoint[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook centralizado para resolución de datos en widgets de gráficas.
 * Elimina el useMemo duplicado en ChartLine, ChartVentasMensual, ChartBarHorizontal y ChartDonut.
 *
 * Resuelve: field_mapping → userOverride → config_schema → heurística (4 capas).
 * Propaga aliases para labels de leyenda y tooltip.
 */
export function useChartData(
  definition: WidgetProps['definition'],
  preference: WidgetProps['preference'],
  period: WidgetProps['period'],
): ChartDataResult {
  const { data, loading, error } = useWidgetFetch<ChartDataPoint[]>(
    definition.widget_id,
    period,
    definition.refresh_policy,
    definition.field_mapping?.rename
  );

  const resolved = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        chartData: [],
        valueKeys: [] as string[],
        categoryField: 'name',
        aliases: {} as Record<string, string>,
      };
    }

    const raw = data as Record<string, unknown>[];
    const userOverride = preference.config_json?.fieldMapping as FieldMappingOverride | undefined;
    const { categoryField, valueFields } = resolveChartFields(
      definition.config_schema,
      raw,
      userOverride,
      definition.field_mapping
    );
    const contract = extractChartContract(definition.config_schema);
    const chartData = aggregateChartData(raw, categoryField, valueFields, contract?.aggregation);
    const aliases = {
      ...(definition.field_mapping?.aliases ?? {}),
      ...(userOverride?.aliases ?? {}),
    };

    return { chartData, valueKeys: valueFields, categoryField, aliases };
  }, [data, definition.config_schema, definition.field_mapping, preference.config_json]);

  return { ...resolved, data, loading, error };
}
